/**
 * Minimal binary reader/writer. Everything on the realtime channel is binary —
 * JSON at 32 snapshots per second per player is an order of magnitude more
 * bandwidth and GC pressure than we can afford.
 */

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

export class Writer {
  private view: DataView;
  private bytes: Uint8Array;
  private offset = 0;

  constructor(initialSize = 1024) {
    this.bytes = new Uint8Array(initialSize);
    this.view = new DataView(this.bytes.buffer);
  }

  private ensure(extra: number): void {
    if (this.offset + extra <= this.bytes.length) return;
    let size = this.bytes.length * 2;
    while (size < this.offset + extra) size *= 2;
    const next = new Uint8Array(size);
    next.set(this.bytes);
    this.bytes = next;
    this.view = new DataView(next.buffer);
  }

  u8(value: number): this {
    this.ensure(1);
    this.view.setUint8(this.offset, value);
    this.offset += 1;
    return this;
  }

  i8(value: number): this {
    this.ensure(1);
    this.view.setInt8(this.offset, value);
    this.offset += 1;
    return this;
  }

  u16(value: number): this {
    this.ensure(2);
    this.view.setUint16(this.offset, value, true);
    this.offset += 2;
    return this;
  }

  i16(value: number): this {
    this.ensure(2);
    this.view.setInt16(this.offset, value, true);
    this.offset += 2;
    return this;
  }

  u32(value: number): this {
    this.ensure(4);
    this.view.setUint32(this.offset, value, true);
    this.offset += 4;
    return this;
  }

  f32(value: number): this {
    this.ensure(4);
    this.view.setFloat32(this.offset, value, true);
    this.offset += 4;
    return this;
  }

  f64(value: number): this {
    this.ensure(8);
    this.view.setFloat64(this.offset, value, true);
    this.offset += 8;
    return this;
  }

  string(value: string): this {
    const encoded = textEncoder.encode(value);
    this.u16(encoded.length);
    this.ensure(encoded.length);
    this.bytes.set(encoded, this.offset);
    this.offset += encoded.length;
    return this;
  }

  finish(): Uint8Array {
    return this.bytes.subarray(0, this.offset);
  }
}

export class Reader {
  private view: DataView;
  private offset = 0;

  constructor(private readonly bytes: Uint8Array) {
    this.view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  }

  get remaining(): number {
    return this.view.byteLength - this.offset;
  }

  u8(): number {
    const value = this.view.getUint8(this.offset);
    this.offset += 1;
    return value;
  }

  i8(): number {
    const value = this.view.getInt8(this.offset);
    this.offset += 1;
    return value;
  }

  u16(): number {
    const value = this.view.getUint16(this.offset, true);
    this.offset += 2;
    return value;
  }

  i16(): number {
    const value = this.view.getInt16(this.offset, true);
    this.offset += 2;
    return value;
  }

  u32(): number {
    const value = this.view.getUint32(this.offset, true);
    this.offset += 4;
    return value;
  }

  f32(): number {
    const value = this.view.getFloat32(this.offset, true);
    this.offset += 4;
    return value;
  }

  f64(): number {
    const value = this.view.getFloat64(this.offset, true);
    this.offset += 8;
    return value;
  }

  string(): string {
    const length = this.u16();
    const slice = this.bytes.subarray(this.offset, this.offset + length);
    this.offset += length;
    return textDecoder.decode(slice);
  }
}

// ---------------------------------------------------------------------------
// Quantisation helpers
// ---------------------------------------------------------------------------

/** Angles are sent as 16-bit fixed point: ~0.005° of precision, plenty for aim. */
export const packAngle = (radians: number): number =>
  Math.round(((radians % (Math.PI * 2)) / (Math.PI * 2)) * 65536) & 0xffff;

/** Returns the angle normalised to (-π, π] so pitch survives the round trip. */
export const unpackAngle = (packed: number): number => {
  const angle = (packed / 65536) * Math.PI * 2;
  return angle > Math.PI ? angle - Math.PI * 2 : angle;
};

/** Positions are sent as 16-bit centimetres, giving ±327m of map with 1cm error. */
export const packPosition = (value: number): number =>
  Math.max(-32768, Math.min(32767, Math.round(value * 100)));

export const unpackPosition = (packed: number): number => packed / 100;
